import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PaymentList from "../../components/Payment/PaymentList";
import PaymentTotal from "../../components/Payment/PaymentTotal";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import HorizontalPadding from "../../components/HorizontalPadding";
import ShipmentOwner from "../../components/Shipment/ShipmentOwner";
import { currencyExchange, useCurrencyExchanges } from "../../utils";
import ShipmentTable, {
  ShipmentField,
} from "../../components/Shipment/ShipmentTable";
import { UserInterface } from "../../interfaces/UserInterface";
import {
  consultBC,
  sendC2PBC,
  sendB2PBC,
  payMerchant,
  savePayment,
  getDocument,
  saveDocument,
  alertService,
  saveProofOfPayment,
  updateDocumentStatus,
  getAccount,
  loaderService,
  updatePayment,
  annulationMerchant,
  mercantilMobilePaymentSearch,
  mercantilTransferSearch,
  mercantilC2P,
  mercantilChange,
  ReintegrationRequest,
  deleteFileDO,
} from "../../services";
import {
  clearPickUp,
  setPickupOwner,
  setPickupTaxes,
  setPickupDocument,
  setPickupShipments,
} from "../../store/slices";
import {
  BackButton,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons";
import {
  TaxInterface,
  DocumentStatus,
  PaymentInterface,
  DocumentInterface,
  PaymentMethodEnum,
  DocumentType,
  PaymentStatusEnum,
  BankAccountInterface,
  PaymentMode,
} from "../../interfaces";
import { PickupModal } from "../../components/Shipment/PickupModal";
import moment from "moment";
import Modal from "../../components/Modal";
import {
  CouponRedeemedAnimation,
  CouponRedeemedValue,
} from "../../components/Shipment/CouponRedeemedAnimation";
import { PaymentCallbackContext } from "../../contexts/PaymentCallbackContext";
import { ChangePaymentModeModal } from "../../components/Shipment/ChangePaymentModeModal";
import { searchLiabilityWaiver } from "../../services/liabilityWaiverServices";
import { getPODDocument } from "../../services/podServices";
import WarningModal from "../../components/WarningModal";
import { merchantLogService } from "../../services/logServices";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";

const Pickup: FC<{}> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = loaderService.useIsLoading();
  const VTIDs = useAppSelector((state) => state.inmutable.vtid);
  const applicationID = useAppSelector(
    (state) => state.inmutable.appData.applicationID
  );

  const exchanges = useCurrencyExchanges();
  const [podSuccess, setPodSuccess] = useState(false);
  const user = useAppSelector((state) => state.user.user);
  const taxes = useAppSelector((state) => state.pickup.taxes);
  const owner = useAppSelector((state) => state.pickup.owner);
  const [redeemedCouponValue, setRedeemedCouponValue] =
    useState<CouponRedeemedValue>(null);
  const coupon = useAppSelector((state) => state.pickup.coupon);
  const userBU = useAppSelector((state) => state.user.businessUnit);
  const document = useAppSelector((state) => state.pickup.document);
  const payments = useAppSelector((state) => state.pickup.payments);
  const buUser = useAppSelector((state) => state.user.businessUnit);
  const shipments = useAppSelector((state) => state.pickup.shipments);
  const [openDeliverModal, setOpenDeliverModal] = useState(false);
  const [openChangePaymentMode, setOpenChangePaymentMode] = useState(false);
  const [liabilityShipments, setLiabilityShipments] = useState<string[]>([]);
  const [openPOPWarningModal, setOpenPOPWarningModal] = useState(false);
  const [openWarningModal, setOpenWarningModal] = useState(false);

  const items = useMemo(() => {
    return shipments
      .map((shipment) => shipment?.items)
      .reduce((acc, curr) => [...acc, ...curr], []);
  }, [shipments]);

  const subTotal = useMemo(() => {
    return +shipments
      .reduce((acc, shipment) => {
        return (
          acc +
          (shipment?.items.reduce((acc, item) => acc + item.rate.value, 0) ?? 0)
        );
      }, 0)
      .toFixed(2);
  }, [shipments]);

  const ipostel = useMemo(() => {
    return (
      document?.taxes.find((tax) => tax.name === "Ipostel")?.value ??
      shipments.reduce((acc, shipment) => {
        return (
          acc + shipment.items.reduce((acc, item) => acc + item.rate.ipostel, 0)
        );
      }, 0)
    );
  }, [shipments, document?.taxes]);

  const iva = useMemo(() => {
    return (
      document?.taxes.find((tax) => tax.name === "IVA")?.value ??
      shipments.reduce((acc, shipment) => {
        return (
          acc + shipment.items.reduce((acc, item) => acc + item.rate.iva, 0)
        );
      }, 0)
    );
  }, [shipments, document?.taxes]);

  const total = useMemo(() => {
    return (
      document?.total ??
      +(
        subTotal +
        taxes?.reduce((acc, tax) => {
          if (tax.type === "percentage") {
            return acc + subTotal * (tax.value / 100);
          }
          return acc + tax.value;
        }, 0)
      ).toFixed(2)
    );
  }, [subTotal, taxes, document?.total]);

  const remaining = useMemo(() => {
    //return document?.baseBalanceAmount ?? to do this we need to get the updated document from DB
    return +(
      total -
      payments
        .filter(
          (p) =>
            p.status !== PaymentStatusEnum.CANCELED &&
            p.status !== PaymentStatusEnum.REJECT
        )
        .reduce((acc, payment) => acc + payment.amount, 0)
    ).toFixed(2);
  }, [total, payments]);

  const validChangeRemaining = useMemo(() => {
    const result =
      (!!document?.total ? document?.total! : total) -
      Number(
        payments
          .filter((p) => p.status === PaymentStatusEnum.APPROVE)
          .reduce((acc, payment) => acc + payment.amount, 0)
          .toFixed(2)
      );
    return result;
  }, [document?.total, payments, total]);

  const paymentMode = useMemo(() => {
    return shipments[0]?.paymentMode;
  }, [shipments]);

  const delivered = useMemo(() => {
    return shipments[0]?.status === 30;
  }, [shipments]);

  const allProofOfPayments = useMemo(() => {
    let allPOP = true;
    payments.forEach((payment) => {
      if (
        [
          PaymentMethodEnum.ZELLE,
          PaymentMethodEnum.PAGO_MOVIL_P2C,
          PaymentMethodEnum.TRANSFERENCIA_BANCARIA_INMEDIATA,
          PaymentMethodEnum.PAGO_MERCHANT_TARJETA_CONCILIACION_MANUAL,
        ].includes(payment.paymentMethod.paymentMethodID) &&
        payment.status === PaymentStatusEnum.PENDING &&
        !payment.proofOfPayment
      ) {
        allPOP = false;
      }
    });

    return allPOP;
  }, [payments]);

  const onRegret = () => {
    dispatch(clearPickUp());
    navigate("/shipments");
  };

  const onSave = async () => {
    // If there are a document, don't save it again
    if (!!document?.documentID) return;

    const status = total < 0.01 ? DocumentStatus.PAID : DocumentStatus.PENDING;

    // Save document
    const documentData: DocumentInterface = {
      documentID: "",
      accountOwner: owner,
      paymentMode: paymentMode!,
      subTotal,
      total,
      status,
      shipments,
      taxes,
      payments: [],
      printed: false,
      hasRetention: false, //document.payments.some((p) => p.isRetention)
      igtfAmount: 0,
      balanceAmount: total,
      documentType: DocumentType.ORDER,
    };
    const response = await saveDocument(
      documentData,
      exchanges,
      buUser?.code,
      user ?? undefined,
      owner,
      coupon?.couponType.couponDescription,
      applicationID
    );
    if (!!response.didError || !response.model?.id_solicitud) {
      alertService.error(
        `Error al guardar el documento`,
        response.errorMessage,
        {
          autoClose: false,
        }
      );
      loaderService.stop();
      return;
    }

    const newDocument = {
      ...documentData,
      documentID: response.model.id_solicitud,
    };

    const documentType =
      status === DocumentStatus.PAID ||
      paymentMode === PaymentMode.DEST_BOXOFFICE_CREDIT
        ? DocumentType.INVOICE
        : DocumentType.ORDER;

    if (documentType === DocumentType.INVOICE) {
      const response = await updateDocumentStatus(
        newDocument.documentID,
        status,
        documentType,
        user ?? undefined,
        status === DocumentStatus.PAID ? userBU?.code : undefined, // set BU invoicer,
        DocumentType.ORDER
      );

      if (!!response.didError) {
        alertService.error(
          "Hubo un error actualizando el estado de la orden",
          response.errorMessage,
          {
            autoClose: false,
          }
        );
      } else {
        newDocument.status = status;
        newDocument.documentType = documentType;
      }
    }

    loaderService.stop();

    return await updateDocument(newDocument);
  };

  const updateDocument = async (document?: DocumentInterface) => {
    if (!document?.documentID) {
      return;
    }

    loaderService.start();

    const documentResponse = await getDocument(document.documentID);
    if (!documentResponse) {
      loaderService.stop();
      return undefined;
    }

    let updatedDocument = {
      ...document!,
      documentNumber: documentResponse.documentNumber,
      payments: documentResponse.payments,
      total: documentResponse.total,
      status: documentResponse.status,
      balanceAmount: documentResponse.balanceAmount,
      baseBalanceAmount: documentResponse.baseBalanceAmount,
      igtfAmount: documentResponse.igtfAmount,
      taxes: documentResponse.taxes,
    };

    loaderService.stop();
    dispatch(setPickupDocument(updatedDocument));

    return updatedDocument;
  };

  const onPay = async (
    payment: PaymentInterface,
    avoidVerifications?: boolean
  ) => {
    const paymentVerified =
      avoidVerifications || user?.roleName === "Superadministrador";
    let result = {
      error: false,
      message: "",
    };

    loaderService.start();
    let savedDocument;

    if (document?.documentID) {
      savedDocument = document;
    } else {
      savedDocument = await onSave();
      if (!savedDocument) {
        loaderService.stop();
        return {
          error: true,
          message: "Error al crear el documento",
        };
      }
    }

    if (
      paymentVerified &&
      payment.paymentMethod.paymentMethodID !== PaymentMethodEnum.MERCHANT
    ) {
      payment.paymentMethod = {
        ...payment.paymentMethod,
        paymentMethodID: Math.abs(payment.paymentMethod.paymentMethodID),
      };
    } else if (
      paymentVerified &&
      payment.paymentMethod.paymentMethodID === PaymentMethodEnum.MERCHANT
    ) {
      payment.paymentMethod = {
        ...payment.paymentMethod,
        paymentMethodID: PaymentMethodEnum.PAGO_MERCHANT_TARJETA_DE_DEBITO,
      };
    }

    // Merchant payment
    else if (
      payment.paymentMethod.paymentMethodID === PaymentMethodEnum.MERCHANT ||
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MERCHANT_P2C ||
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MERCHANT_TARJETAS_DE_CREDITO ||
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MERCHANT_TARJETA_DE_DEBITO ||
      payment.paymentMethod.paymentMethodID === PaymentMethodEnum.ZELLE
    ) {
      let ci = payment.clientIdentifier!;
      let amount = currencyExchange(payment.amount, exchanges, "BS");
      let zelleDescription;

      if (payment.paymentMethod.paymentMethodID === PaymentMethodEnum.ZELLE) {
        zelleDescription = payment.clientIdentifier!;
        amount = currencyExchange(payment.amount, exchanges, "USD");
        payment.destBankID = 32;
      } else {
        payment.paymentMethod.bankID = 27;
        payment.paymentMethod.currencyID = 1;
        payment.paymentMethod.paymentMethodID =
          PaymentMethodEnum.PAGO_MERCHANT_TARJETA_DE_DEBITO;
      }

      const res = await payMerchant(amount, ci, zelleDescription);

      if (!res || res.codRespuesta !== "00") {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: res?.mensajeRespuesta ?? "No hubo respuesta de Merchant",
        };
      } else if (res.tipoProducto === "P2C") {
        payment.paymentMethod.paymentMethodID =
          PaymentMethodEnum.PAGO_MERCHANT_P2C;
        payment.paymentMethod.bankID = 4;
        payment.reference = res?.numeroReferencia ?? "";
      } else if (res.tipoProducto === "ZELLE") {
        payment.reference = `${res?.numeroReferencia ?? ""}:${
          res?.numSeq ?? ""
        }:${res?.terminalVirtual ?? ""}`;
      } else {
        payment.reference = `${res?.numSeq ?? ""}:${
          res?.terminalVirtual ?? ""
        }:${res?.numeroAutorizacion}`;
        payment.status = PaymentStatusEnum.PENDING;
      }

      if (
        res?.codRespuesta === "00" &&
        (res?.numSeq === 0 ||
          res?.terminalVirtual === "" ||
          res?.numeroAutorizacion === "")
      ) {
        const request = JSON.stringify({
          amount,
          clientIdentifier: ci,
          zelleDescription,
        });
        await merchantLogService({
          request: request,
          response: JSON.stringify(res),
          endpoint: "Merchant payment",
          responseMessage: res?.mensajeRespuesta,
        });
      }
    }

    // P2C payment - Bancaribe
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_P2C &&
      payment.destBankID === 6
    ) {
      const res = await consultBC(
        payment.clientIdentifier!,
        currencyExchange(payment.amount, exchanges, "BS"),
        payment.phone!,
        payment.reference!,
        "PM",
        ""
      );

      if (!res?.success) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: !!res?.message
            ? res.message.code + " " + res.message.message
            : "Error al procesar el pago",
        };
      } else {
        payment.reference = res?.codigoConfirmacion;
      }
    }

    // P2C payment - Mercantil
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_P2C &&
      payment.destBankID === 4
    ) {
      const res = await mercantilMobilePaymentSearch(
        payment.phone!,
        moment(payment.paymentDate).toDate(),
        payment.reference!,
        currencyExchange(payment.amount, exchanges, "BS"),
        payment.attempts ?? 1
      );

      if (res?.didError || res.model?.transaction_list?.length === 0) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.model?.error_list && res.model?.error_list.length > 0) {
          errorMessage = res.model?.error_list
            .map((error) => error.description)
            .join(", ");
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el pago",
        };
      } else if (
        res.model?.transaction_list &&
        res.model?.transaction_list?.length > 0
      ) {
        payment.reference = res?.model?.transaction_list[0].payment_reference;
      }
    }

    // TRF payment - Bancaribe
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.TRANSFERENCIA_BANCARIA_INMEDIATA &&
      payment.destBankID === 6
    ) {
      const res = await consultBC(
        payment.clientIdentifier!,
        currencyExchange(payment.amount, exchanges, "BS"),
        "",
        payment.reference!,
        "TRF",
        ""
      );

      if (!res?.success) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: !!res?.message
            ? res.message.code + " " + res.message.message
            : "Error al procesar el pago",
        };
      } else {
        payment.reference = res?.codigoConfirmacion;
      }
    }

    // TRF payment - Mercantil
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.TRANSFERENCIA_BANCARIA_INMEDIATA &&
      payment.destBankID === 4
    ) {
      const res = await mercantilTransferSearch(
        payment.clientIdentifier!,
        moment(payment.paymentDate).toDate(),
        payment.bank!,
        payment.reference!,
        currencyExchange(payment.amount, exchanges, "BS"),
        payment.attempts ?? 1
      );

      if (res?.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.model?.errorList && res.model?.errorList.length > 0) {
          errorMessage = res.model?.errorList
            .map((error) => error.description)
            .join(", ");
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el pago",
        };
      }
      if (
        res.model?.transferSearchList &&
        res.model?.transferSearchList?.length > 0
      ) {
        if (res.model?.transferSearchList[0].trxStatus === "R") {
          payment.status = PaymentStatusEnum.REJECT;
          result = {
            error: true,
            message: "La transferencia fue rechazada por el banco",
          };
        } else if (
          res.model?.transferSearchList[0].previouslySearched === "S"
        ) {
          payment.status = PaymentStatusEnum.REJECT;
          result = {
            error: true,
            message: "El pago ya fue verificado previamente",
          };
        }
      }
    }

    // C2P payment - Bancaribe
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_C2P &&
      payment.destBankID === 6
    ) {
      const res = await sendC2PBC(
        payment.bank!,
        payment.clientIdentifier!,
        currencyExchange(payment.amount, exchanges, "BS"),
        payment.phone!,
        payment.otp
      );

      if (res?.codigoError !== 0) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: res?.descripcionError ?? "Error al procesar el pago",
        };
      }
      payment.reference = res?.secuencial.toString();
    }

    // C2P payment - Mercantil
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_C2P &&
      payment.destBankID === 4
    ) {
      const res = await mercantilC2P(
        payment.phone!,
        payment.clientIdentifier!,
        payment.otp!,
        savedDocument?.documentID!.split("-")[0]!,
        payment.amount,
        payment.bank!
      );
      if (res?.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.model?.error_list && res.model?.error_list.length > 0) {
          errorMessage = res.model?.error_list
            .map((error) => error.description)
            .join(", ");
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el pago",
        };
      } else {
        payment.reference = `${res?.model?.transaction_c2p_response?.payment_reference}:${res?.model?.transaction_c2p_response?.invoice_number}`;
      }
    }

    // POS payment
    else if (
      payment.paymentMethod.paymentMethodID === PaymentMethodEnum.PUNTO_DE_VENTA
    ) {
      payment.reference = `${payment.affiliateNumber}:${payment.batchNumber}`;
    }

    // B2P change payment - Bancaribe
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO &&
      payment.destBankID === 6
    ) {
      const res = await sendB2PBC(
        payment.bank!,
        payment.clientIdentifier!,
        payment.amount,
        payment.phone!,
        document ? `Asociado a orden #${document.documentID}` : "Pago 1 B2P",
        "Factura"
      );

      if (res?.codigoError !== 0) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: res?.descripcionError ?? "Error al procesar el pago",
        };
      } else {
        payment.reference = res?.codigoConfirmacion;
      }
    }

    // B2P change payment - Mercantil
    else if (
      payment.paymentMethod.paymentMethodID ===
        PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO &&
      payment.destBankID === 4
    ) {
      const res = await mercantilChange(
        payment.phone!,
        payment.clientIdentifier!,
        document?.documentID!.split("-")[0]!,
        payment.amount,
        payment.bank!
      );
      if (res?.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        let errorMessage = "";
        if (res.model?.error_list && res.model?.error_list.length > 0) {
          errorMessage = res.model?.error_list
            .map((error) => error.description)
            .join(", ");
        }
        result = {
          error: true,
          message: errorMessage || "Error al procesar el pago",
        };
      } else {
        payment.reference = `${res?.model?.transaction_c2p_response?.payment_reference}:${res?.model?.transaction_c2p_response?.invoice_number}`;
      }
    }

    // Reintegration
    if (
      payment.paymentMethod.paymentMethodID === PaymentMethodEnum.REINTEGRO &&
      payment.status === PaymentStatusEnum.PENDING
    ) {
      const res = await ReintegrationRequest(
        user?.userName!,
        userBU!,
        document!,
        payment.amount,
        document?.accountOwner!
      );

      payment.paymentMethod.bankAccountID = undefined;

      if (res.didError) {
        payment.status = PaymentStatusEnum.REJECT;
        result = {
          error: true,
          message: res.errorMessage,
        };
      } else {
        payment.status = PaymentStatusEnum.PENDING;
      }
    }

    // verifies that the payment was not verified before
    let verified = false;
    if (
      payment.status !== PaymentStatusEnum.REJECT &&
      payment.paymentMethod.paymentMethodID !== PaymentMethodEnum.EFECTIVO &&
      !paymentVerified
    )
      document?.payments.forEach((p) => {
        if (
          p.reference === payment.reference &&
          p.reference !== "" &&
          p.status !== PaymentStatusEnum.REJECT
        ) {
          verified = true;
        }
      });

    if (verified) {
      payment.status = PaymentStatusEnum.REJECT;
      result = {
        error: true,
        message: "El pago ya fue verificado previamente",
      };
    }

    // Convert amount to negative if change
    if (
      [
        PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO,
        PaymentMethodEnum.VUELTO_EFECTIVO,
        PaymentMethodEnum.REINTEGRO,
      ].includes(payment.paymentMethod.paymentMethodID)
    ) {
      payment.amount = -payment.amount;
      payment.paymentAmount = -(payment.paymentAmount ?? 0);
    }

    // Change currency
    let currencyId = payment.paymentMethod.currencyID;

    const response = await savePayment(
      payment,
      savedDocument!,
      userBU?.code,
      user ?? undefined,
      currencyId
    );
    if (!!response.didError) {
      loaderService.stop();
      alertService.error("Error al guardar el pago", response.errorMessage, {
        autoClose: false,
      });
      return {
        error: true,
        message: "Error al guardar el pago",
      };
    }

    // Update document
    const oldBalance =
      savedDocument?.payments
        .filter(
          (p) =>
            p.status !== PaymentStatusEnum.CANCELED &&
            p.status !== PaymentStatusEnum.REJECT
        )
        .reduce((acc, payment) => acc + payment.amount, 0) ?? 0;
    const updatedDocument = await updateDocument(savedDocument);
    const newBalance =
      updatedDocument?.payments
        .filter(
          (p) =>
            p.status !== PaymentStatusEnum.CANCELED &&
            p.status !== PaymentStatusEnum.REJECT
        )
        .reduce((acc, payment) => acc + payment.amount, 0) ?? 0;

    if (
      oldBalance < (updatedDocument?.total ?? 0) &&
      newBalance >= (updatedDocument?.total ?? 0)
    ) {
      setPodSuccess(true);
    }

    loaderService.stop();

    return result;
  };

  const onSaveProofOfPayment = async (
    file: File,
    retention: PaymentInterface,
    user: UserInterface,
    statusID: number,
    reference?: string,
    isModification?: boolean
  ) => {
    loaderService.start();
    const saved = await saveProofOfPayment(
      file,
      retention,
      user,
      statusID,
      reference,
      isModification,
      document?.documentID
    );

    if (saved.didError || !saved.model) {
      loaderService.stop();
      alertService.error(
        "Error al guardar el comprobante de pago",
        saved.errorMessage,
        {
          autoClose: false,
        }
      );
      return;
    }
    alertService.success("El comprobante se guardó exitosamente");

    const newPayments = payments.map((payment) => {
      if (payment !== retention) return payment;

      return {
        ...payment,
        proofOfPayment: saved.model!,
      };
    });

    const updatedDocument: DocumentInterface = {
      ...document!,
      payments: newPayments,
    };
    // const response = await saveDocument(
    //   updatedDocument,
    //   exchanges,
    //   buUser?.code,
    //   user ?? undefined,
    //   undefined,
    //   undefined,
    //   applicationID
    // );
    // if (!!response.didError) {
    //   alertService.error("Error al guardar la orden");
    //   return;
    // }
    loaderService.stop();
    dispatch(
      setPickupDocument({
        ...updatedDocument,
        // documentID: response.model!.id_solicitud,
      })
    );
  };

  const onDeleteProofOfPayment = async (retention: PaymentInterface) => {
    loaderService.start();

    const response = await deleteFileDO(retention.proofOfPayment!);

    if (response.didError || !response.model) {
      loaderService.stop();
      alertService.error(
        "Error al eliminar el comprobante de pago",
        response.errorMessage,
        {
          autoClose: false,
        }
      );
      return;
    }

    alertService.success("El comprobante se eliminó exitosamente");

    const newPayments = payments.map((payment) => {
      if (payment !== retention) return payment;

      return {
        ...payment,
        proofOfPayment: undefined,
      };
    });

    const update = await updatePayment({
      documentID: document?.documentID ?? retention.documentID!,
      paymentDetailID: retention.paymentDetailID!,
      statusID: PaymentStatusEnum.PENDING,
      user: user!,
    });

    if (update.didError) {
      alertService.error(
        "Error al eliminar el comprobante",
        update.errorMessage,
        {
          autoClose: false,
        }
      );
      loaderService.stop();
      return;
    }

    const updatedDocument: DocumentInterface = {
      ...document!,
      payments: newPayments,
    };

    loaderService.stop();
    dispatch(
      setPickupDocument({
        ...updatedDocument,
      })
    );
  };

  const onCancelRetention = async (retention: PaymentInterface) => {
    loaderService.start();
    const response = await updatePayment({
      documentID: document?.documentID ?? retention.documentID!,
      paymentDetailID: retention.paymentDetailID!,
      statusID: PaymentStatusEnum.CANCELED,
      user: user!,
    });

    if (!!response.didError) {
      alertService.error(
        "Error al cancelar la retención",
        response.errorMessage,
        {
          autoClose: false,
        }
      );
      loaderService.stop();
      return;
    }

    await updateDocument(document);

    loaderService.stop();
  };

  const onCancelPayment = async (transaction: PaymentInterface) => {
    loaderService.start();

    if (VTIDs.length === 0) {
      alertService.error("No se pudo obtener el VTID");
      loaderService.stop();
      return;
    }

    if (transaction.reference) {
      const paymentVtid = transaction.reference.split(":")[1];

      if (VTIDs.includes(paymentVtid)) {
        const numSeq = transaction.reference.split(":")[0];
        const res = await annulationMerchant(numSeq);

        if (res !== null && res.codRespuesta === "00") {
          const response = await updatePayment({
            documentID: document?.documentID ?? transaction.documentID!,
            paymentDetailID: transaction.paymentDetailID!,
            statusID: PaymentStatusEnum.CANCELED,
            user: user!,
          });

          if (response.didError) {
            alertService.error(
              "Error al cancelar el pago",
              response.errorMessage,
              {
                autoClose: false,
              }
            );
            loaderService.stop();
            return;
          }
          alertService.success("El pago se anuló exitosamente");

          const documentResponse = await getDocument(document!.documentID);
          if (!documentResponse) {
            alertService.error("Error al obtener la orden");
            loaderService.stop();
            return;
          }

          const totalPaid = documentResponse.payments
            .filter(
              (p) =>
                p.status !== PaymentStatusEnum.CANCELED &&
                p.status !== PaymentStatusEnum.REJECT
            )
            .reduce((acc, payment) => acc + payment.amount, 0);
          const remaining = total - totalPaid;
          const status =
            (documentResponse.balanceAmount ?? remaining) < 0.01
              ? DocumentStatus.PAID
              : DocumentStatus.PARTIAL_PAID;

          let updatedDocument = {
            ...document!,
            payments: documentResponse.payments,
            total: documentResponse.total ?? total,
            status,
            balanceAmount: documentResponse.balanceAmount,
            baseBalanceAmount: documentResponse.baseBalanceAmount,
          };

          // Update document status
          if (document?.status !== status) {
            const response = await updateDocumentStatus(
              updatedDocument.documentID,
              status,
              undefined,
              user ?? undefined,
              status === DocumentStatus.PAID ? userBU?.code : undefined, // set BU invoicer,
              updatedDocument.documentType
            );
            if (!!response.didError) {
              alertService.error(
                "Hubo un error actualizando el estado de la orden",
                response.errorMessage,
                {
                  autoClose: false,
                }
              );
            } else {
              updatedDocument.status = status;
            }
          }

          loaderService.stop();
          dispatch(setPickupDocument(updatedDocument));
          return;
        }
        alertService.error(
          "No se pudo anular el pago",
          !!res ? `Código de error: ${res.codRespuesta}` : undefined,
          {
            autoClose: false,
          }
        );
        loaderService.stop();
        return;
      }
      alertService.error("Este pago no se proceso con el terminal actual");
      loaderService.stop();
      return;
    }
    alertService.error(
      "La operación no tiene terminal asociado, no se puede anular"
    );
    loaderService.stop();
  };

  const onApproveReintegration = async (
    transaction: PaymentInterface,
    bankAccount?: BankAccountInterface
  ) => {
    loaderService.start();
    const response = await updatePayment({
      documentID: document!.documentID,
      paymentDetailID: transaction.paymentDetailID!,
      bankAccountID: bankAccount?.bankAccountID,
      sourceBankID: bankAccount?.bankID,
      statusID: PaymentStatusEnum.APPROVE,
      user: user!,
    });

    if (response.didError) {
      alertService.error(
        "No se pudo aprobar el reintegro",
        response.errorMessage,
        {
          autoClose: false,
        }
      );
      loaderService.stop();
      return;
    }
    alertService.success("Reintegro aprobado con éxito");
    const documentResponse = await getDocument(document!.documentID);
    if (!documentResponse) {
      alertService.error("Error al obtener la orden");
      loaderService.stop();
      return;
    }

    const totalPaid = documentResponse.payments
      .filter(
        (p) =>
          p.status !== PaymentStatusEnum.CANCELED &&
          p.status !== PaymentStatusEnum.REJECT
      )
      .reduce((acc, payment) => acc + payment.amount, 0);
    const remaining = total - totalPaid;
    const status =
      (documentResponse.balanceAmount ?? remaining) < 0.01
        ? DocumentStatus.PAID
        : DocumentStatus.PARTIAL_PAID;

    let updatedDocument = {
      ...document!,
      payments: documentResponse.payments,
      total: documentResponse.total ?? total,
      status,
      balanceAmount: documentResponse.balanceAmount,
      baseBalanceAmount: documentResponse.baseBalanceAmount,
    };

    // Update document status
    if (document?.status !== status) {
      const response = await updateDocumentStatus(
        updatedDocument.documentID,
        status,
        undefined,
        user ?? undefined,
        status === DocumentStatus.PAID ? userBU?.code : undefined, // set BU invoicer,
        updatedDocument.documentType
      );
      if (response.didError) {
        alertService.error(
          "Hubo un error actualizando el estado de la orden",
          response.errorMessage,
          {
            autoClose: false,
          }
        );
      } else {
        updatedDocument.status = status;
      }
    }

    loaderService.stop();
    dispatch(setPickupDocument(updatedDocument));
  };

  const onChangePaymentMode = async () => {
    setOpenChangePaymentMode(true);
  };

  const handleDownloadPOD = async () => {
    if (liabilityShipments.length === 0) {
      alertService.error("No existe declaración jurada firmada digitalmente.");
      return;
    }

    loaderService.start();
    alertService.info("Descargando documentos...");

    const fileResult = await getPODDocument(liabilityShipments);

    loaderService.stop();
    if (fileResult) {
      const fileURL = URL.createObjectURL(fileResult.file);

      const a = window.document.createElement("a");
      a.href = fileURL;
      a.download = fileResult.fileName;
      a.click();

      URL.revokeObjectURL(fileURL);
      alertService.success("Documento impreso satisfactoriamente.");
    } else {
      alertService.error("No se pudo descargar el documento del POD.");
    }
  };

  useEffect(() => {
    const taxes: TaxInterface[] = [
      {
        name: "IVA",
        value: iva,
        type: "fixed",
      },
      {
        name: "Ipostel",
        value: ipostel,
        type: "fixed",
      },
    ];

    if (!!document?.igtfAmount) {
      taxes.push({
        name: "IGTF",
        value: document.igtfAmount,
        type: "fixed",
      });
    }

    dispatch(setPickupTaxes(taxes));
  }, [ipostel, iva, document, dispatch]);

  useEffect(() => {
    const updateAccount = async (id: string) => {
      return await getAccount(id);
    };

    const updateAccounts = async () => {
      const newShipments = await Promise.all(
        shipments.map(async (shipment) => {
          const shipper = (await updateAccount(shipment.shipper?.id ?? ""))!;
          const consignee = (await updateAccount(
            shipment.consignee?.id ?? ""
          ))!;
          return {
            ...shipment,
            shipper,
            consignee,
          };
        })
      );

      dispatch(setPickupShipments(newShipments));
    };

    updateAccounts();

    // Do not add shipments to the dependency list to avoid an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (coupon) {
      if (coupon.couponValue === 100) {
        setRedeemedCouponValue(100);
      } else if (coupon.couponValue === 50) {
        setRedeemedCouponValue(50);
      }
    }
  }, [coupon]);

  useEffect(() => {
    const getLiabilityShipments = async () => {
      let liabilityShipments: string[] = [];

      for (let shipment of shipments) {
        const response = await searchLiabilityWaiver({
          shipmentHeaderID: shipment.id,
        });
        if (!!response.didError || !response.model) {
          alertService.error(
            `Hubo un error al buscar la declaración jurada de la guía ${shipment.shipmentNumber}.`,
            response.errorMessage,
            {
              autoClose: false,
            }
          );
          loaderService.stop();
          return;
        }
        if (response.model.length > 0) {
          if (!response.model[0].isPhysicalLiabilityWaiver)
            liabilityShipments.push(shipment.shipmentNumber!);
        }
      }
      setLiabilityShipments(liabilityShipments);
    };

    getLiabilityShipments();
  }, [shipments]);

  useEffect(() => {
    updateDocument(document);
  }, []);

  return (
    <main className="lg:pl-72 pb-32">
      <div className="py-6 sm:px-6 lg:px-8 bg-white relative flex items-center justify-between h-32">
        <div>
          <header className="ml-4 text-2xl font-bold text-gray-700 ">
            Entregar Paquetes
          </header>
        </div>
      </div>

      <HorizontalPadding paddingTop>
        <div className="flex flex-1 flex-col gap-8">
          {/* Header */}
          {(!document || document?.status === DocumentStatus.PENDING) && (
            <div className="flex w-full">
              <BackButton handleOnRegret={onRegret} />
            </div>
          )}
          {/* Show invoice owner */}
          <ShipmentOwner
            owner={owner}
            onSelectOwner={(owner) => dispatch(setPickupOwner(owner))}
          />

          {/* Show shipments resumen */}
          <div className={"pr-1"} style={{ flex: 1 }}>
            <ShipmentTable
              exchanges={exchanges}
              fields={[
                ShipmentField.ID,
                ShipmentField.SHIPPER,
                ShipmentField.BU_SOURCE,
                ShipmentField.CONSIGNEE,
                ShipmentField.STATUS,
                ShipmentField.AMOUNT,
              ]}
              showDetails
              shipments={shipments.map((s) => s!)}
            />
          </div>

          {/* Show totals */}
          <PaymentTotal
            exchanges={exchanges}
            taxes={taxes}
            subTotal={subTotal}
            remaining={remaining}
            paymentMode={paymentMode}
            total={document?.total}
            onChangePaymentMode={onChangePaymentMode}
            allowPaymentModeChange={!document}
          />

          {/* Show payments */}
          {!!owner?.fiscalAddress ? (
            <PaymentCallbackContext.Provider
              value={{
                onFinishPayment: () =>
                  podSuccess && !delivered && setOpenDeliverModal(true),
              }}
            >
              <PaymentList
                editable
                items={items}
                payments={payments}
                remaining={remaining}
                validChangeRemaining={validChangeRemaining}
                document={document}
                owner={owner}
                exchanges={exchanges}
                paymentMode={document?.paymentMode ?? shipments[0].paymentMode}
                onPay={onPay}
                onCancelRetention={onCancelRetention}
                onCancelPayment={onCancelPayment}
                onApproveReintegration={onApproveReintegration}
                onSaveProofOfPayment={onSaveProofOfPayment}
                onDeleteProofOfPayment={onDeleteProofOfPayment}
              />
            </PaymentCallbackContext.Provider>
          ) : (
            <div className="flex flex-col gap-4 bg-white rounded-lg border px-8 pb-6 pt-4">
              <p className="text-gray-400">
                Se necesita conocer la dirección fiscal de la persona a facturar
                para poder agregar pagos
              </p>
            </div>
          )}

          {(remaining < 0.01 ||
            paymentMode === PaymentMode.DEST_BOXOFFICE_CREDIT) && (
            <div className="flex w-full justify-end">
              <PrimaryButton
                className="w-40"
                disabled={loading}
                onClick={async () => {
                  loaderService.start();
                  if (validChangeRemaining > -0.1) {
                    if (!allProofOfPayments) setOpenPOPWarningModal(true);
                    else {
                      await onSave();
                      if (!delivered) setOpenDeliverModal(true);
                    }
                  } else {
                    setOpenWarningModal(true);
                  }
                  loaderService.stop();
                }}
              >
                FINALIZAR
              </PrimaryButton>
            </div>
          )}
        </div>
      </HorizontalPadding>

      <PickupModal
        openModal={openDeliverModal}
        setOpenModal={setOpenDeliverModal}
        shipments={shipments}
        consignee={shipments[0]?.consignee}
        shipmentNumbers={shipments.map((s) => s?.shipmentNumber ?? "")}
        liabilityShipments={liabilityShipments}
        onDownloadPOD={handleDownloadPOD}
        onSubmit={() => navigate(`/documents/${document?.documentID}`)}
      />

      {/* Proof of Payment Warning Modal */}
      <WarningModal
        openModal={openPOPWarningModal}
        setOpenModal={setOpenPOPWarningModal}
        message="Es necesario agregar todos los comprobantes de los pagos a conciliar para poder finalizar"
        onPrimaryButton={() => setOpenPOPWarningModal(false)}
        showSecondaryButton={false}
      />

      {/* Coupon redeemed animation: modal GIF + confetti + audio with fade in/out */}
      <CouponRedeemedAnimation
        couponValue={redeemedCouponValue}
        onClose={() => setRedeemedCouponValue(null)}
      />

      <ChangePaymentModeModal
        exchanges={exchanges}
        shipments={shipments}
        document={document}
        open={openChangePaymentMode}
        setOpen={setOpenChangePaymentMode}
      />

      {/* Change warning modal */}
      <Modal openModal={openWarningModal} setOpenModal={setOpenWarningModal}>
        <div
          className="flex flex-col items-center justify-center"
          style={{ maxWidth: "20rem" }}
        >
          <div className="flex flex-col items-center justify-center w-full">
            <ExclamationTriangleIcon className="w-32 h-32" />
          </div>
          <p className="mt-2 text-lg text-center text-gray-700">
            ¿Está seguro que desea finalizar la orden con un cambio pendiente?
          </p>
          <div className="mt-4 flex flex-row justify-center gap-12">
            <SecondaryButton
              className="px-4"
              onClick={() => setOpenWarningModal(false)}
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton
              className="px-4"
              disabled={loading}
              onClick={async () => {
                if (!allProofOfPayments) {
                  setOpenPOPWarningModal(true);
                } else {
                  setOpenWarningModal(false);
                  await onSave();
                  if (!delivered) setOpenDeliverModal(true);
                }
              }}
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </main>
  );
};

export default Pickup;
